/**
 * API 客户端模块
 * 
 * 基于 Axios 封装的 HTTP 请求客户端
 * 集成 Sa-Token 认证，使用 token header 传递认证令牌
 */
import { useBizLineStore } from '@/stores/biz-line-store'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

/* ============================================
 * 常量定义
 * ============================================ */

/** 认证令牌 localStorage 键名 */
export const AUTH_TOKEN_KEY = 'admin_auth_token'

/** 用户信息 localStorage 键名 */
export const USER_INFO_KEY = 'admin_user_info'

/** API 来源（空字符串 = 同源，子域名模式下为 api 域名） */
const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''

/** API 基础路径（不含业务线前缀，业务线在拦截器中动态拼接） */
const BASE_URL = '/admin/api'

/** 请求超时时间（毫秒） */
const TIMEOUT = 30000

/* ============================================
 * 类型定义
 * ============================================ */

/** 通用 API 响应结构 */
export interface ApiResponse<T = unknown> {
  /** 状态码（字符串类型，"10000" 表示成功） */
  code: string
  /** 响应消息 */
  message: string
  /** 响应数据 */
  data: T
}

/** 分页请求参数 */
export interface PaginationParams {
  /** 当前页码，从 1 开始 */
  pageNum: number
  /** 每页条数 */
  pageSize: number
}

/** 分页响应数据 */
export interface PaginationData<T> {
  /** 数据列表 */
  list: T[]
  /** 总条数 */
  total: number
  /** 当前页码 */
  pageNum: number
  /** 每页条数 */
  pageSize: number
  /** 总页数 */
  totalPages: number
}

/* ============================================
 * 工具函数
 * ============================================ */

/**
 * 获取存储的认证令牌
 * @returns 认证令牌或 null
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

/**
 * 存储认证令牌
 * @param token - 认证令牌
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token)
}

/**
 * 移除认证令牌
 */
export function removeAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY)
}

/**
 * 获取存储的用户信息
 * @returns 用户信息对象或 null
 */
export function getUserInfo<T = unknown>(): T | null {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY)
  if (!userInfoStr) return null

  try {
    return JSON.parse(userInfoStr) as T
  } catch {
    return null
  }
}

/**
 * 存储用户信息
 * @param userInfo - 用户信息对象
 */
export function setUserInfo<T>(userInfo: T): void {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo))
}

/**
 * 移除用户信息
 */
export function removeUserInfo(): void {
  localStorage.removeItem(USER_INFO_KEY)
}

/**
 * 清除所有认证相关信息
 */
export function clearAuthData(): void {
  removeAuthToken()
  removeUserInfo()
}

/* ============================================
 * Axios 实例创建
 * ============================================ */

/** 创建 Axios 实例 */
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

/* ============================================
 * 请求拦截器
 * ============================================ */

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 动态拼接业务线前缀到 baseURL
    const bizLine = useBizLineStore.getState().bizLine
    config.baseURL = `${API_ORIGIN}/${bizLine}/admin/api`

    // 获取并设置认证令牌
    // Admin 端 tokenName = "token-admin"
    const token = getAuthToken()
    if (token && config.headers) {
      config.headers['token-admin'] = token
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/* ============================================
 * 响应拦截器
 * ============================================ */

apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response

    // 检查业务状态码
    // code === "10000" 表示成功（后端返回的是字符串）
    if (data.code === '10000') {
      return response
    }

// 业务错误处理
    const errorMessage = data.message || '请求失败'

    // 特定错误码处理
    switch (data.code) {
      case '10009':
        clearAuthData()
        setTimeout(() => {
          window.location.href = '/login'
        }, 100)
        break
      case '10008':
        // 权限不足
        console.error('权限不足，无法访问该资源')
        break
      default:
        // 其他业务错误
        console.error(`业务错误 [${data.code}]: ${errorMessage}`)
    }

    return Promise.reject(new Error(errorMessage))
  },
  (error) => {
    // HTTP 错误处理
    let errorMessage = '网络错误，请稍后重试'

    if (error.response) {
      // 服务器返回错误响应
      const { status, data } = error.response

      switch (status) {
        case 400:
          errorMessage = '请求参数错误'
          break
        case 401:
          errorMessage = '登录已过期，请重新登录'
          clearAuthData()
          setTimeout(() => {
            window.location.href = '/login'
          }, 100)
          break
        case 403:
          errorMessage = '权限不足，无法访问'
          break
        case 404:
          errorMessage = '请求的资源不存在'
          break
        case 500:
          errorMessage = '服务器内部错误'
          break
        case 502:
          errorMessage = '网关错误'
          break
        case 503:
          errorMessage = '服务暂时不可用'
          break
        case 504:
          errorMessage = '网关超时'
          break
        default:
          errorMessage = data?.message || `请求失败 (${status})`
      }
    } else if (error.request) {
      // 请求已发出但未收到响应
      errorMessage = '网络连接失败，请检查网络'
    } else {
      // 请求配置错误
      errorMessage = error.message || '请求配置错误'
    }

    console.error('HTTP 错误:', errorMessage)
    return Promise.reject(new Error(errorMessage))
  }
)

/* ============================================
 * 请求方法封装
 * ============================================ */

/**
 * GET 请求
 * @param url - 请求路径
 * @param params - 查询参数
 * @param config - Axios 配置
 * @returns 响应数据
 */
export async function get<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(url, {
    params,
    ...config,
  })
  return response.data.data
}

/**
 * POST 请求
 * @param url - 请求路径
 * @param data - 请求体数据
 * @param config - Axios 配置
 * @returns 响应数据
 */
export async function post<T>(
  url: string,
  data?: Record<string, unknown> | unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config)
  return response.data.data
}

/**
 * PUT 请求
 * @param url - 请求路径
 * @param data - 请求体数据
 * @param config - Axios 配置
 * @returns 响应数据
 */
export async function put<T>(
  url: string,
  data?: Record<string, unknown> | unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config)
  return response.data.data
}

/**
 * DELETE 请求
 * @param url - 请求路径
 * @param params - 查询参数
 * @param config - Axios 配置
 * @returns 响应数据
 */
export async function del<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(url, {
    params,
    ...config,
  })
  return response.data.data
}

/**
 * PATCH 请求
 * @param url - 请求路径
 * @param data - 请求体数据
 * @param config - Axios 配置
 * @returns 响应数据
 */
export async function patch<T>(
  url: string,
  data?: Record<string, unknown> | unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config)
  return response.data.data
}

/* ============================================
 * 导出
 * ============================================ */

export { apiClient }
export default apiClient